import './SocialIcons.scss';
import classNames from 'classnames';
import { Icon, IconName, IconSize } from '@components/icon';
import { Link, Text } from '@components/text';

export interface SocialItemData {
  name: IconName;
  href: string;
  label: string;
}

export interface SocialIconsProps {
  items?: Array<SocialItemData>;
  label?: string;
  onClick?: (event?: React.MouseEvent<HTMLDivElement>) => void;
  className?: string;
  size?: IconSize;
  centered?: boolean;
  linkComponent?: React.ElementType;
}

/**
 * SocialIcons
 * Wrapper component for footer social icons
 *
 * @param items - array of social items
 * @param label - label for the social icons
 * @param onClick - click handler
 * @param className - optional additional class name
 * @param size - size of the icons
 * @param centered - whether to center align the icons and label
 * @param linkComponent - Custom component to render links instead of a native `<a>` (e.g. Next.js Link, React Router Link).
 */
export const SocialIcons: React.FC<SocialIconsProps> = ({
  items,
  label,
  onClick,
  className,
  size = 'large',
  centered = false,
  linkComponent,
}) => {
  return (
    <div
      className={classNames('vds-footer-socials', className, {
        'vds-footer-socials--centered': centered,
      })}
      onClick={onClick}>
      {label && (
        <Text
          component="h2"
          variant="body-02"
          color="secondary"
          weight="semibold"
          size="small"
          className="vds-footer-socials__label">
          {label}
        </Text>
      )}
      <ul className="vds-footer-socials__list">
        {items?.map(({ name, href, label }, i) => (
          <li key={i}>
            <Link
              href={href}
              aria-label={label}
              target="_blank"
              color="tertiary"
              isFooter
              linkComponent={linkComponent}
              className="vds-footer-socials__link">
              <Icon name={name} size={size} />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};
